import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, Eye, Layers, Search, Check, Star, ChevronDown, ChevronRight, Code, AlertTriangle, Maximize2, Minimize2, CheckCircle2 } from 'lucide-react';
import api from '../../../services/api';
import * as AssessmentBuilderAPI from '../../../services/assessmentBuilderApi';
import toast from 'react-hot-toast';
import TemplatePreview from './TemplatePreview';
import { DEVICE_DIMENSIONS, TEMPLATE_QUESTION_TYPES } from './constants';

const TemplateManagerModal = ({
  isOpen,
  onClose,
  assessmentId,
  onTemplateUpdated,
  preloadedTemplates,
  appliedDefaultTemplateIds = [],
  selectedElementData,
  applyQuestionTemplate,
  applyTemplateToAllOfType,
  screens,
  currentScreenIndex,
  deviceMode
}) => {
  const [templates, setTemplates] = useState(preloadedTemplates || { system: [], sponsor: [], user: [], assessment: [] });
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name } or null
  const [deleteTypedName, setDeleteTypedName] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuestionType, setSelectedQuestionType] = useState('all');
  const [settingDefault, setSettingDefault] = useState(null);

  // Derive device category from the assessment's current deviceMode
  const currentDeviceCategory = DEVICE_DIMENSIONS[deviceMode]?.category || 'desktop';
  const currentCanvasDims = DEVICE_DIMENSIONS[deviceMode] || DEVICE_DIMENSIONS['desktop-standard'] || { width: 1200, height: 800 };
  const [selectedDevice, setSelectedDevice] = useState(currentDeviceCategory);

  const QUESTION_TYPE_LABELS = {
    likert: 'Ordinal (Likert)',
    rating: 'Rating',
    rating_vas: 'VAS',
    rating_nrs: 'NRS',
    text: 'Text',
    yes_no: 'Yes / No',
    nominal: 'Nominal',
    dropdown: 'Dropdown',
    date: 'Date',
    body_map: 'Body Map',
  };

  // Compute a more granular display category for templates
  // VAS and NRS are both stored as 'rating' in the backend — differentiate by name
  const getDisplayCategory = (template) => {
    if (template.question_type !== 'rating') return template.question_type;
    const name = (template.name || '').toLowerCase();
    if (name.includes('vas') || name.includes('visual analog')) return 'rating_vas';
    if (name.includes('nrs') || name.includes('numeric rating')) return 'rating_nrs';
    return 'rating';
  };

  const DEVICE_CATEGORIES = [
    { value: 'all', label: 'All Devices' },
    { value: 'phone', label: 'Mobile' },
    { value: 'tablet', label: 'Tablet' },
    { value: 'desktop', label: 'Desktop' },
  ];

  const selectedElementType = selectedElementData?.data?.type;
  const hasSelectedQuestion = selectedElementData?.type === 'question' && !!applyQuestionTemplate;

  // Reset device filter to current assessment device when modal opens
  useEffect(() => {
    if (isOpen) setSelectedDevice(currentDeviceCategory);
  }, [isOpen]);

  // Update templates when preloaded data changes, or fetch if empty when modal opens
  useEffect(() => {
    const hasPreloaded = preloadedTemplates && (
      preloadedTemplates.system?.length > 0 ||
      preloadedTemplates.sponsor?.length > 0 ||
      preloadedTemplates.user?.length > 0 ||
      preloadedTemplates.assessment?.length > 0
    );

    if (hasPreloaded) {
      setTemplates(preloadedTemplates);
    } else if (isOpen && assessmentId) {
      fetchAllTemplates();
    }
  }, [preloadedTemplates, isOpen]);

  const fetchAllTemplates = async () => {
    setLoading(true);
    try {
      const questionTypes = TEMPLATE_QUESTION_TYPES;
      const allTemplatesMap = { system: [], sponsor: [], user: [], assessment: [] };
      const appliedIds = new Set();

      for (const qType of questionTypes) {
        try {
          const response = await api.get('/api/assessment-builder/layout-templates/available/', {
            params: { question_type: qType, assessment_id: assessmentId }
          });
          if (response.data.system_templates) allTemplatesMap.system.push(...response.data.system_templates);
          if (response.data.sponsor_templates) allTemplatesMap.sponsor.push(...response.data.sponsor_templates);
          if (response.data.user_templates) allTemplatesMap.user.push(...response.data.user_templates);
          if (response.data.assessment_templates) allTemplatesMap.assessment.push(...response.data.assessment_templates);
          if (response.data.assessment_templates_from_other_assessments) {
            allTemplatesMap.assessment.push(...response.data.assessment_templates_from_other_assessments.map(a => a.template));
          }
          if (response.data.applied_default_template_id) {
            appliedIds.add(response.data.applied_default_template_id);
          }
        } catch (err) {
          console.error(`Failed to fetch templates for ${qType}:`, err);
        }
      }

      setTemplates({
        system: Array.from(new Map(allTemplatesMap.system.map(t => [t.id, t])).values()),
        sponsor: Array.from(new Map(allTemplatesMap.sponsor.map(t => [t.id, t])).values()),
        user: Array.from(new Map(allTemplatesMap.user.map(t => [t.id, t])).values()),
        assessment: Array.from(new Map(allTemplatesMap.assessment.map(t => [t.id, t])).values()),
        appliedDefaultIds: [...appliedIds]
      });
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestDelete = (template) => {
    setDeleteConfirm({ id: template.id, name: template.name });
    setDeleteTypedName('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const response = await api.delete(`/api/assessment-builder/layout-templates/${deleteConfirm.id}/`);
      if (response.data?.deleted_type === 'soft') {
        toast.success('Template removed — it was in use by other assessments so the data is preserved');
      } else {
        toast.success('Template deleted');
      }
      setDeleteConfirm(null);
      setDeleteTypedName('');
      await fetchAllTemplates();
      if (onTemplateUpdated) onTemplateUpdated();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const handleApply = (template) => {
    if (!applyQuestionTemplate) return;
    applyQuestionTemplate(template);
    toast.success(`Applied "${template.name}"`);
    onClose();
  };

  const handleSetDefault = async (template) => {
    if (!assessmentId) return;
    setSettingDefault(template.id);
    try {
      if (applyTemplateToAllOfType) {
        await applyTemplateToAllOfType(template);
      } else {
        await AssessmentBuilderAPI.setLayoutDefault({
          template_id: template.id,
          assessment_id: assessmentId,
          question_type: template.question_type
        });
      }
      toast.success(`"${template.name}" applied to all existing ${template.question_type || 'question'} questions and set as default`);
      if (onTemplateUpdated) onTemplateUpdated();
      onClose();
    } catch (error) {
      console.error('Failed to set default:', error);
      toast.error('Failed to set as default');
    } finally {
      setSettingDefault(null);
    }
  };

  const getTemplateDeviceCategory = (template) => template?.device_category || 'all';

  const filterTemplates = (templateList) => {
    return templateList.filter(t => {
      const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
      const displayCategory = getDisplayCategory(t);
      const matchesType = selectedQuestionType === 'all' || displayCategory === selectedQuestionType;
      const matchesDevice = selectedDevice === 'all' ||
        getTemplateDeviceCategory(t) === selectedDevice ||
        getTemplateDeviceCategory(t) === 'all';
      return matchesSearch && matchesType && matchesDevice;
    });
  };

  const allTemplates = [...templates.system, ...templates.sponsor, ...(templates.user || []), ...templates.assessment];
  const questionTypes = [...new Set(allTemplates.map(t => getDisplayCategory(t)).filter(Boolean))].sort();

  // Build question data for live preview, keyed by question_type.
  // Prefers selected element; falls back to first matching element on current screen.
  const getQuestionDataForType = (questionType) => {
    // 1. Try selected element
    if (selectedElementData?.type === 'question') {
      if (selectedElementData.data?.type === questionType) {
        const item = selectedElementData.itemData || selectedElementData.data || {};
        const text = item.text || item.question_text;
        const options = item.options || item.response_options;
        if (text || options) return { text, options };
      }
    }
    // 2. Fall back to first matching element on current screen
    const currentScreen = screens?.[currentScreenIndex];
    if (!currentScreen) return null;
    for (const el of currentScreen.elements || []) {
      if (el.type !== 'question') continue;
      const elType = el.data?.type || el.itemData?.type;
      if (elType !== questionType) continue;
      const item = el.itemData || el.data || {};
      const text = item.text || item.question_text;
      const options = item.options || item.response_options;
      if (text || options) return { text, options };
    }
    return null;
  };

  // Identify applied default template IDs — prefer prop from localAssessmentConfig (always current),
  // fall back to cache-derived IDs from the API response
  const appliedDefaultIds = new Set(
    appliedDefaultTemplateIds.length > 0
      ? appliedDefaultTemplateIds
      : (templates.appliedDefaultIds || [])
  );

  // Collect applied templates from all categories
  const appliedTemplates = allTemplates.filter(t => appliedDefaultIds.has(t.id));

  // Remaining templates per category (exclude applied ones)
  const yourTemplates = [...(templates.user || []), ...(templates.assessment || [])].filter(t => !appliedDefaultIds.has(t.id));
  const remainingSystem = (templates.system || []).filter(t => !appliedDefaultIds.has(t.id));
  const remainingSponsor = (templates.sponsor || []).filter(t => !appliedDefaultIds.has(t.id));

  const renderSection = (title, list, canDelete, { icon, accent } = {}) => {
    const filtered = filterTemplates(list);
    if (filtered.length === 0) return null;
    return (
      <div>
        <div className="flex items-center gap-2 mb-2 px-1">
          {icon}
          <h3 className={`text-[13px] font-semibold ${accent || 'text-gray-900 dark:text-gray-50'}`}>{title}</h3>
          <span className="badge badge-gray tabular-nums">{filtered.length}</span>
        </div>
        <div className="space-y-2">
          {filtered.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              canDelete={canDelete}
              canApply={hasSelectedQuestion}
              isMatchingType={hasSelectedQuestion && template.question_type === selectedElementType}
              isApplied={appliedDefaultIds.has(template.id)}
              isSettingDefault={settingDefault === template.id}
              onDelete={handleRequestDelete}
              onApply={handleApply}
              onSetDefault={handleSetDefault}
              questionData={getQuestionDataForType(template.question_type)}
              canvasDims={currentCanvasDims}
            />
          ))}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-gray-950/40 dark:bg-black/70 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-overlay ring-1 ring-gray-900/5 dark:ring-white/10 w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.06]">
          <div className="flex items-start gap-2.5">
            <Layers className="mt-0.5 h-5 w-5 flex-shrink-0 text-gray-400" strokeWidth={1.75} />
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Template Manager</h2>
              <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
                {hasSelectedQuestion
                  ? `Selected: ${selectedElementType} question — apply or set defaults below`
                  : 'Select a question on the canvas to enable Apply'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-sm btn-icon -mr-2 flex-shrink-0">
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b border-gray-100 dark:border-white/[0.06] space-y-2.5">
          {/* Row 1: Search + Question Type */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input h-9 pl-9"
              />
            </div>
            <select
              value={selectedQuestionType}
              onChange={(e) => setSelectedQuestionType(e.target.value)}
              className="input h-9 w-auto pr-9"
            >
              <option value="all">All Question Types</option>
              {questionTypes.map(type => (
                <option key={type} value={type}>
                  {QUESTION_TYPE_LABELS[type] || type}
                </option>
              ))}
            </select>
          </div>
          {/* Row 2: Device toggle — defaults to assessment's current device */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Device:</span>
            {DEVICE_CATEGORIES.filter(c => c.value !== 'all').map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setSelectedDevice(value)}
                className={`px-2.5 py-1 text-xs rounded-full font-medium ring-1 ring-inset transition-colors ${
                  selectedDevice === value
                    ? 'bg-primary-50 text-primary-700 ring-primary-600/20 dark:bg-primary-400/10 dark:text-primary-300 dark:ring-primary-400/25'
                    : 'text-gray-600 ring-gray-200 hover:bg-gray-50 dark:text-gray-300 dark:ring-white/10 dark:hover:bg-white/[0.04]'
                }`}
              >
                {label}
                {value === currentDeviceCategory && selectedDevice !== value ? ' *' : ''}
              </button>
            ))}
            <button
              onClick={() => setSelectedDevice('all')}
              className={`px-2.5 py-1 text-xs rounded-full font-medium ring-1 ring-inset transition-colors ${
                selectedDevice === 'all'
                  ? 'bg-primary-50 text-primary-700 ring-primary-600/20 dark:bg-primary-400/10 dark:text-primary-300 dark:ring-primary-400/25'
                  : 'text-gray-500 ring-gray-200 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:ring-white/10 dark:hover:bg-white/[0.04]'
              }`}
            >
              Show All
            </button>
          </div>
        </div>

        {/* Template List */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">Loading templates...</div>
          ) : (
            <div className="space-y-6">
              {renderSection(
                'Applied to This Assessment',
                appliedTemplates,
                false,
                {
                  icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />,
                  accent: 'text-gray-900 dark:text-gray-50'
                }
              )}
              {renderSection('Your Templates', yourTemplates, true)}
              {renderSection('Sponsor Templates', remainingSponsor, false)}
              {renderSection('System Templates', remainingSystem, false)}
              {allTemplates.length === 0 && (
                <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No templates found</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 rounded-b-2xl border-t border-gray-100 bg-gray-50/70 px-6 py-3.5 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-950/50 dark:bg-black/75 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-overlay ring-1 ring-gray-900/5 dark:ring-white/10 w-full max-w-md p-6">
            <div className="flex items-center gap-2.5 mb-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 text-danger-600 dark:text-danger-400" strokeWidth={1.75} />
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">Delete Template</h3>
            </div>
            <p className="text-[13.5px] leading-relaxed text-gray-600 dark:text-gray-300 mb-4">
              This action cannot be undone. To confirm, type the template name below:
            </p>
            <div className="rounded-lg border border-gray-200/70 bg-gray-50 px-3 py-2 mb-3 dark:border-white/[0.07] dark:bg-white/[0.03]">
              <span className="font-mono text-[13px] font-medium text-gray-800 dark:text-gray-200">{deleteConfirm.name}</span>
            </div>
            <input
              type="text"
              placeholder="Type template name to confirm..."
              value={deleteTypedName}
              onChange={(e) => setDeleteTypedName(e.target.value)}
              className="input mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setDeleteConfirm(null); setDeleteTypedName(''); }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteTypedName !== deleteConfirm.name}
                className="btn btn-danger"
              >
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

const TemplateCard = ({ template, canDelete, canApply, isMatchingType, isApplied, isSettingDefault, onDelete, onApply, onSetDefault, questionData, canvasDims }) => {
  const [expandedView, setExpandedView] = useState(null); // null | 'preview' | 'json'
  const [fullscreen, setFullscreen] = useState(false);

  const toggle = (view) => setExpandedView(prev => prev === view ? null : view);

  return (
    <div className={`rounded-xl border bg-white shadow-card transition-colors dark:bg-gray-800 ${
      isApplied ? 'border-emerald-300/80 dark:border-emerald-500/30' :
      isMatchingType ? 'border-primary-300 dark:border-primary-400/40' :
      'border-gray-200/80 hover:border-gray-300 dark:border-white/[0.07] dark:hover:border-white/15'
    }`}>
      <div className="px-4 py-3 flex items-center gap-3">
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-[13.5px] font-semibold text-gray-900 dark:text-gray-50">{template.name}</h4>
            <span className="badge badge-gray">
              {template.question_type}
            </span>
            {isApplied && (
              <span className="badge badge-dot badge-success">
                Active
              </span>
            )}
            {template.device_category && template.device_category !== 'all' && (
              <span className="badge badge-gray">
                {template.device_category}
              </span>
            )}
          </div>
          {template.description && (
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">{template.description}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Apply */}
          {canApply && (
            <button
              onClick={() => onApply(template)}
              className={`btn btn-sm ${
                isMatchingType
                  ? 'btn-primary'
                  : 'btn-secondary'
              }`}
              title={isMatchingType ? 'Apply to selected question' : 'Type mismatch — apply anyway?'}
            >
              <Check className="w-3.5 h-3.5" />
              Apply
            </button>
          )}

          {/* Use for Assessment */}
          <button
            onClick={() => onSetDefault(template)}
            disabled={isSettingDefault}
            className="btn btn-secondary btn-sm"
            title={`Automatically apply this template when adding new ${template.question_type || 'question'} questions to this assessment`}
          >
            <Star className="w-3.5 h-3.5" />
            {isSettingDefault ? '...' : 'Use for Assessment'}
          </button>

          {/* Preview */}
          <button
            onClick={() => toggle('preview')}
            className={`btn btn-sm btn-icon ${expandedView === 'preview' ? 'bg-primary-50 text-primary-700 dark:bg-primary-400/10 dark:text-primary-300' : 'btn-ghost'}`}
            title="Preview"
          >
            <Eye className="w-4 h-4" />
          </button>

          {/* JSON */}
          <button
            onClick={() => toggle('json')}
            className={`btn btn-sm btn-icon ${expandedView === 'json' ? 'bg-primary-50 text-primary-700 dark:bg-primary-400/10 dark:text-primary-300' : 'btn-ghost'}`}
            title="View JSON config"
          >
            <Code className="w-4 h-4" />
          </button>

          {/* Delete */}
          {canDelete && (
            <button
              onClick={() => onDelete(template)}
              className="btn btn-danger-soft btn-sm btn-icon"
              title="Delete template"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {expandedView === 'preview' && (
        <div className="border-t border-gray-100 bg-gray-50 p-4 rounded-b-xl dark:border-white/[0.06] dark:bg-white/[0.02]">
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setFullscreen(true)}
              className="btn btn-ghost btn-sm h-7 px-2 text-xs"
              title="Fullscreen preview at actual canvas size"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              Fullscreen
            </button>
          </div>
          <TemplatePreview template={template} questionData={questionData} />
        </div>
      )}

      {expandedView === 'json' && (
        <div className="px-4 pb-3 border-t border-gray-100 dark:border-white/[0.06]">
          <pre className="mt-3 rounded-lg bg-gray-50 p-3 font-mono text-xs text-gray-600 dark:bg-white/[0.03] dark:text-gray-300 overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(template.layout_config, null, 2)}
          </pre>
        </div>
      )}

      {/* Fullscreen preview overlay */}
      {fullscreen && createPortal(
        <div
          className="fixed inset-0 bg-gray-950/70 dark:bg-black/80 flex items-center justify-center z-[10001] p-4"
          onClick={() => setFullscreen(false)}
        >
          <div
            className="relative rounded-2xl bg-white p-6 shadow-overlay ring-1 ring-gray-900/5 dark:bg-gray-800 dark:ring-white/10 max-h-[95vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">{template.name}</h3>
                <p className="mt-1 text-[13px] text-gray-500 dark:text-gray-400">
                  {canvasDims ? `${canvasDims.width}×${canvasDims.height}px` : 'Actual canvas size'} preview
                  {questionData ? ' — using question data' : ' — sample data'}
                </p>
              </div>
              <button
                onClick={() => setFullscreen(false)}
                className="btn btn-secondary btn-sm"
              >
                <Minimize2 className="w-3.5 h-3.5" />
                Close
              </button>
            </div>
            <TemplatePreview
              template={template}
              questionData={questionData}
              maxWidth={9999}
              canvasOverride={canvasDims ? { width: canvasDims.width, height: canvasDims.height } : undefined}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};


export default TemplateManagerModal;
